import React, { useCallback, useEffect, useState } from 'react';
import { formatPrice } from 'utils/price';
import styled from 'styled-components';

import { LineItemContainer } from 'pages/Totem/Aside/Basket/LineItemContainer';

import { OptimizedImage } from 'components/OptimizedImage';

import { debounce, getGrams, getKgs, pluralize } from 'utils';
import { roundPrice } from 'utils/orders';
import {
    GetCategoriesQuery,
    GetOrdersOfTotemQuery,
    ProductFlow,
    RequestedOrderProductFragmentFragment,
    useLessProductMutation,
    useMoreProductMutation,
    useRemoveProductMutation,
    useSetQuantityMutation,
} from 'data/__generated__';
import { CardActionButton, CardButtonGroup, QuantityInputStyle, RemoveIcon } from 'styles/components/card.styles';
import { CardActions } from 'data/functions/card-actions';
import { useApolloClient } from '@apollo/client';
import { ClipLoader } from 'react-spinners';
import { Minus, Plus } from 'styled-icons/boxicons-regular';
import { colors } from 'styles/theme';
import { MODIFIABLE_ORDER_STATES } from 'constants/states-app';

interface BasketProductItemProps {
    borderBottom?: boolean;
    borderTop?: boolean;
    paddingLeft?: string | number;
    product: RequestedOrderProductFragmentFragment;
    order?: NonNullable<GetOrdersOfTotemQuery['ordersOfTotem']['recentOrders']>[number] | null;
    currentCategory?: GetCategoriesQuery['clientCategories'][number] | undefined;
    readOnly?: boolean;
}

const getWeightDisplay = ({
    quantity,
    displayAsKilograms,
    weight,
}: {
    quantity: number;
    displayAsKilograms: boolean;
    weight: number;
}): string => {
    if (!displayAsKilograms) {
        return `${quantity} ${pluralize('pièce', quantity)}`;
    }
    const kilograms = getKgs(displayAsKilograms, weight, quantity);
    const grams = getGrams(displayAsKilograms, weight, quantity);
    return `${kilograms}kg${grams ? ` ${grams}g` : ''}`;
};

type HandlerKey = 'add' | 'remove' | 'more' | 'less';

export const BasketProductItem: React.FC<BasketProductItemProps> = ({
    borderBottom,
    borderTop,
    paddingLeft,
    product,
    order,
    currentCategory,
    readOnly,
}) => {
    // Check if the order is modifiable based on its state
    const isOrderModifiable = order?.state ? MODIFIABLE_ORDER_STATES.includes(order.state) : false;

    // Check if caterer products are locked (catererLocked flag + product is CATERER flow)
    const isCatererProductLocked = order?.catererLocked && product.flow === ProductFlow.Caterer;

    // Product modification is disabled if order is not modifiable OR if caterer product is locked
    const isProductModificationDisabled = !isOrderModifiable || isCatererProductLocked;

    // shouldProductHaveBeenDelivered tells us whether or not a product has been delivered or was supposed to be delivered
    // (quantityPicked is null before the delivery)
    const shouldProductHaveBeenDelivered = !!product.quantityPicked || product.quantityPicked === 0;
    // GraphQL mutations
    const [removeProduct, removeProductData] = useRemoveProductMutation();
    const [lessProduct, lessProductData] = useLessProductMutation();
    const [moreProduct, moreProductData] = useMoreProductMutation();
    const [setQuantity] = useSetQuantityMutation();
    const apolloClient = useApolloClient();
    const [quantityInput, setQuantityInput] = useState(product?.quantityPicked ?? product?.quantity ?? 0);

    const emptyMutationResult = {
        loading: false,
        error: undefined,
        data: undefined,
        called: false,
        client: apolloClient,
        reset: () => {},
    };

    const handlers = {
        addProduct: {
            handler: () => Promise.resolve(),
            data: emptyMutationResult,
        },
        removeProduct: {
            handler: removeProduct,
            data: removeProductData,
        },
        moreProduct: {
            handler: moreProduct,
            data: moreProductData,
        },
        lessProduct: {
            handler: lessProduct,
            data: lessProductData,
        },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const delayedSetQuantity = useCallback(
        debounce((mutationOptions) => setQuantity(mutationOptions), 500),
        [],
    );

    const { handleRemoveProduct, handleMoreProduct, handleLessProduct } = CardActions(
        { product, order, currentCategory, debouncedFunction: delayedSetQuantity },
        handlers,
    );

    const synchronizeQuantity = (quantity: number) => () => {
        setQuantityInput(quantity);
    };
    const handleSetQuantity: React.ChangeEventHandler<HTMLInputElement> = ({ target: { value = 0 } }) => {
        setQuantityInput(Number(value));

        if (!value) {
            delayedSetQuantity.cancel();
            return;
        }
        delayedSetQuantity({
            variables: {
                orderId: order?._id,
                productId: product?._id,
                categoryId: currentCategory?._id,
                quantity: Math.round(Number(value)),
            },
            refetchQueries: [],
        });
    };

    // numberOfProductsNotDelivered is equal to 0 if the delivery has not been done yet, and equal to the difference between
    // quantity and quantity picked if the delivery is complete
    const numberOfProductsNotDelivered =
        product?.quantityPicked || product?.quantityPicked === 0
            ? (typeof product?.quantity === 'number' ? product.quantity : 0) - product.quantityPicked
            : 0;
    // Always pass a number for quantity to getWeightDisplay
    const pickedQuantity =
        shouldProductHaveBeenDelivered && typeof product?.quantityPicked === 'number'
            ? product?.quantityPicked
            : typeof product?.quantity === 'number'
              ? product.quantity
              : 0;

    const unitPrice = product.unitPrice ?? 0;
    const quantity = product.quantity ?? 0;

    const originalTotalPrice = unitPrice * quantity;

    const updatedTotalPrice =
        shouldProductHaveBeenDelivered && numberOfProductsNotDelivered
            ? roundPrice(originalTotalPrice - unitPrice * numberOfProductsNotDelivered)
            : originalTotalPrice;

    const shouldShowPrice = updatedTotalPrice > 0;

    useEffect(() => {
        setQuantityInput(pickedQuantity);
    }, [pickedQuantity]);

    function isLoading(keys: HandlerKey[]): boolean {
        return keys.some((key) => {
            const handlerObj = handlers[`${key}Product` as keyof typeof handlers];
            return handlerObj && handlerObj.data.loading;
        });
    }
    return (
        <LineItemContainer
            as="div"
            $borderBottom={borderBottom}
            $borderTop={borderTop}
            $paddingLeft={paddingLeft}
            $clickable={false}
        >
            <ProductDetails>
                <ImageWrapper>
                    <OptimizedImage src={product.imageUrl ?? ''} width={80} blurOnLoad={false} />
                    {!readOnly && (
                        <RemoveButton
                            onClick={handleRemoveProduct}
                            data-test="product-remove-cart-button"
                            disabled={isProductModificationDisabled}
                        >
                            <RemoveIcon size="18px" />
                        </RemoveButton>
                    )}
                </ImageWrapper>
                <ProductInfo>
                    <NameAndPriceLine>
                        <WeightAndActionsRow>
                            <ProductName
                                $shouldBeGreyed={
                                    shouldProductHaveBeenDelivered &&
                                    !!numberOfProductsNotDelivered &&
                                    numberOfProductsNotDelivered === product.quantity
                                }
                            >
                                {product.name}
                            </ProductName>
                            <QuantityRow>
                                {' '}
                                {readOnly ? (
                                    <>
                                        {shouldProductHaveBeenDelivered && !!numberOfProductsNotDelivered && (
                                            <CrossedValue>
                                                {getWeightDisplay({
                                                    quantity:
                                                        typeof product.quantity === 'number' ? product.quantity : 0,
                                                    displayAsKilograms: product.displayKg ?? false,
                                                    weight: product.weightWithoutPackaging ?? 0,
                                                })}
                                            </CrossedValue>
                                        )}
                                        {numberOfProductsNotDelivered ? (
                                            <UpdatedValue>
                                                &#8594;{' '}
                                                {getWeightDisplay({
                                                    quantity: pickedQuantity,
                                                    displayAsKilograms: product.displayKg ?? false,
                                                    weight: product.weightWithoutPackaging ?? 0,
                                                })}
                                            </UpdatedValue>
                                        ) : (
                                            <div>
                                                {getWeightDisplay({
                                                    quantity: pickedQuantity,
                                                    displayAsKilograms: product.displayKg ?? false,
                                                    weight: product.weightWithoutPackaging ?? 0,
                                                })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <CardButtonGroup style={{ gap: '4px', height: '50px' }}>
                                        <CardActionButton
                                            $marginMultiplier={[0, 0.15, 0, 0]}
                                            disabled={
                                                isLoading(['more', 'less', 'add']) || isProductModificationDisabled
                                            }
                                            $isLoading={isLoading(['more', 'less', 'add'])}
                                            onClick={handleLessProduct}
                                            $borderRadius={[3, 3, 3, 3]}
                                            maxWidth="50px"
                                            data-test="basket-less-button"
                                        >
                                            {isLoading(['less']) ? (
                                                <ClipLoader color={colors.black} size="20px" />
                                            ) : (
                                                <Minus size="20px" />
                                            )}
                                        </CardActionButton>
                                        <QuantityInputStyle
                                            type="number"
                                            value={quantityInput || ''}
                                            onChange={handleSetQuantity}
                                            onBlur={synchronizeQuantity(pickedQuantity ?? 0)}
                                            fontSize="1.4rem"
                                            minWidth="80px"
                                            data-test="quantity-cart-input"
                                        />
                                        <CardActionButton
                                            $marginMultiplier={[0, 0, 0, 0.15]}
                                            disabled={
                                                isLoading(['more', 'less', 'add']) || isProductModificationDisabled
                                            }
                                            onClick={handleMoreProduct}
                                            $isLoading={isLoading(['more', 'less', 'add'])}
                                            $borderRadius={[3, 3, 3, 3]}
                                            maxWidth="50px"
                                            data-test="basket-more-button"
                                        >
                                            {isLoading(['more']) ? (
                                                <ClipLoader color={colors.black} size="20px" />
                                            ) : (
                                                <Plus size="20px" />
                                            )}
                                        </CardActionButton>
                                    </CardButtonGroup>
                                )}
                            </QuantityRow>
                        </WeightAndActionsRow>
                        {shouldShowPrice && (
                            <ProductPrice>
                                {shouldProductHaveBeenDelivered && !!numberOfProductsNotDelivered && (
                                    <CrossedValue>{formatPrice(unitPrice * quantity)}</CrossedValue>
                                )}
                                {shouldProductHaveBeenDelivered && numberOfProductsNotDelivered ? (
                                    <UpdatedValue>
                                        &#8594;{' '}
                                        {formatPrice(
                                            roundPrice(unitPrice * quantity - unitPrice * numberOfProductsNotDelivered),
                                        )}
                                    </UpdatedValue>
                                ) : (
                                    formatPrice(unitPrice * quantity)
                                )}
                            </ProductPrice>
                        )}
                    </NameAndPriceLine>
                    <WeightAndTextLine>
                        {!!numberOfProductsNotDelivered && (
                            <ProductPriceInfo>
                                {numberOfProductsNotDelivered === product.quantity
                                    ? '(produit épuisé)'
                                    : '(quantités insuffisantes)'}
                            </ProductPriceInfo>
                        )}
                        {isCatererProductLocked && (
                            <CatererLockedInfo>Commande traiteur validée, non modifiable</CatererLockedInfo>
                        )}
                    </WeightAndTextLine>
                </ProductInfo>
            </ProductDetails>
        </LineItemContainer>
    );
};

type BasketProductItemPropsCss = {
    $shouldBeGreyed?: boolean;
    $hasMissingQuantities?: boolean | number;
};

const UpdatedValue = styled.div`
    color: ${({ theme }) => theme.colors.red};
`;

const ProductDetails = styled.div`
    display: flex;
    width: 100%;
`;

const ProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const NameAndPriceLine = styled.div`
    margin-left: 5px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const WeightAndTextLine = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 5px 0 0 5px;
`;

const ProductPrice = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 150px;
`;

const ProductPriceInfo = styled.div`
    color: ${({ theme }) => theme.colors.red};
    text-align: right;
`;

const CatererLockedInfo = styled.div`
    color: ${({ theme }) => theme.colors.pantinGrey};
    font-size: 0.85rem;
    font-style: italic;
`;

const ProductName = styled.div<BasketProductItemPropsCss>`
    color: ${({ theme, $shouldBeGreyed }) => $shouldBeGreyed && theme.colors.pantinGrey};
    font-weight: 500;
`;

const CrossedValue = styled.div`
    color: ${({ theme }) => theme.colors.pantinGrey};
    text-decoration-color: ${({ theme }) => theme.colors.pantinGrey};
    margin-right: 5px;
`;

const WeightAndActionsRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
`;

const ImageWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
    /* width: 100px; */
`;

const RemoveButton = styled.button`
    position: absolute;
    top: -10px;
    left: 4px;

    width: 20px;
    height: 20px;

    border-radius: 50%;
    background: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.white};

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
    border: none;
    padding: 0;

    &:hover {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const QuantityRow = styled.div`
    margin-top: 6px;
    width: 70%;
`;
